/* eslint complexity: ["error", 15] */
import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { useColorModeValue, useToast } from '@chakra-ui/react';
import {
  ColumnFiltersState,
  ExpandedState,
  Row,
  RowSelectionState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  FilterFn,
  ColumnOrderState,
  ColumnDef,
} from '@tanstack/react-table';

import { flatMap, fromPairs, isNil, partition } from 'lodash';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

// local imports
import { useMutation } from 'urql';
import {
  ColumnType,
  OrderDeepFragment,
  PredicateType,
  ProjectDeepFragment,
  UpdateOrderColumnDocument,
  UpdateOrderDocument,
  OrderColumnDeepFragment,
  OrderColumnPatch,
  EventResource,
  EventType,
} from '../../../../gql/graphql';

import { ResolvedOrder } from '../../../../hooks/order';
import { ResolvedOrderEntry } from '../../../../domain/resolve-order-entry';
import { QuantityWithUnits } from '../../../../domain/property-types';
import { useUserTenant } from '../../../../services/auth-info';

import { formatQuantity } from '../../../../utils/quantity-utils';
import {
  columnNameToColumnId,
  columnIdToColumnName,
  columnIdToColumn,
  columnTypeToId,
} from '../utils';
import { exportTableToExcel } from '../../../../utils/excel-utils';
import { useProjectHistory } from '../../project-history/hooks/useProjectHistory';
import { getAggregatedOrderDiffingTableRows } from '../../project-history/utils/diffing';
import { mergeEntries } from '../../project-history/hooks/useDiffing';
import { getColumns, OrderEntryTableRow } from './columns';
import { processTableData } from './oob-table-processing';
import { createRegexFromInputString } from 'src/domain/property-operations';
import { useSelection } from 'src/services/viewer/selection';
import { useViewManager } from 'src/components/common/ViewManagerContext';
import { useEventHistory } from 'src/hooks/event-history';

type FilterValueType = {
  predicate: PredicateType;
  predicateValues: string[];
}[];

type DefaultColumnType =
  | ColumnType.Color
  | ColumnType.Description
  | ColumnType.LinkedCount
  | ColumnType.ParentOrder
  | ColumnType.ParentOrderStatus
  | ColumnType.Quantity
  | ColumnType.Total
  | ColumnType.Unit
  | ColumnType.UnitPrice;

interface ExtendedOrderColumn extends OrderColumnDeepFragment {
  originalKey?: string;
}

// Define ColumnMapping type
type ColumnMapping = { id: string; newName: string };

// Extend OrderDeepFragment to include columnNameMapping
export interface ExtendedOrder extends OrderDeepFragment {
  columnNameMapping?: Record<string, ColumnMapping>;
}

interface UseAggregatedOrderTableProps {
  resolvedOrders: Array<ResolvedOrder>;
  thisOrder: ExtendedOrder;
  project: ProjectDeepFragment;
  grouping: Array<string>;
  shouldFlatten: boolean;
  handleRowSelection: (
    selectedRows: Row<OrderEntryTableRow>[],
    allRows: Row<OrderEntryTableRow>[]
  ) => void;
  setGrouping: Dispatch<SetStateAction<Array<string>>>;
  isIsolationToggleActive: boolean;
}

export const useAggregatedOrderTable = ({
  resolvedOrders,
  thisOrder,
  project,
  grouping,
  shouldFlatten,
  setGrouping,
  handleRowSelection,
  isIsolationToggleActive,
}: UseAggregatedOrderTableProps) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { isProtan } = useUserTenant();
  const [aggregation, setAggregation] = useState(() => {
    const aggArray = thisOrder.aggregatedOrderAggregation?.map(
      (agg) =>
        [
          columnNameToColumnId(
            agg.columnName!,
            thisOrder.orderColumns,
            agg.columnType
          ),
          agg.aggregationValue,
        ] as [string, string]
    );

    return aggArray ? Object.fromEntries(aggArray) : {};
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [, updateOrderColumn] = useMutation(UpdateOrderColumnDocument);
  const [, updateOrder] = useMutation(UpdateOrderDocument);
  const toast = useToast();
  const { t: translation } = useTranslation('table');
  const [renamingColumnId, setRenamingColumnId] = useState<string | null>(null);

  const data: OrderEntryTableRow[] = useMemo(() => {
    if (shouldFlatten) {
      return resolvedOrders.flatMap((order: ResolvedOrder) =>
        transFormAndFlattenOrderEntries(order, thisOrder.id)
      );
    }
    return resolvedOrders.flatMap((order: ResolvedOrder) =>
      order.orderEntries.map((orderEntry) =>
        transformOrderEntry(orderEntry, order, thisOrder.id)
      )
    );
  }, [resolvedOrders, shouldFlatten, thisOrder.id]);

  useEffect(() => {
    // NOTE: Ensure that resolvedOrders is not empty when running the contents of this hook
    // Also, we refrain from running if we are in history mode
    if (resolvedOrders.length <= 0 || isHistoryDrawerOpen) {
      return;
    }

    async function cleanupAndMakeColumns() {
      // We are excluding the aggregated orders to we do not need to add the clause `&& col.orderId !== thisOrder.id` to prevent matching with ourselves
      const relevantResolvedOrders = resolvedOrders.filter(
        (order) => !order.isAggregatedOrder
      );

      // Create a map of existing columns indexed by their original names
      // This will help us preserve renamed columns
      const existingColumnsByOriginalName = new Map();

      // CUSTOM COLUMNS
      const allCustomColumns = relevantResolvedOrders.flatMap(
        (order: ResolvedOrder) =>
          order.orderColumns.filter(
            (col) => col.columnType === ColumnType.Custom
          )
      );
      const customColumnsInThisOrder = thisOrder.orderColumns.filter(
        (col) => col.columnType === ColumnType.Custom
      );

      // Store existing column IDs by their name to preserve renamed columns
      customColumnsInThisOrder.forEach((col) => {
        // Get all custom columns with the same name from source orders
        const sourceColumns = allCustomColumns.filter(
          (sourceCol) => sourceCol.name === col.name
        );
        // If we found matching source columns, associate them with this column
        if (sourceColumns.length > 0) {
          sourceColumns.forEach((sourceCol) => {
            existingColumnsByOriginalName.set(sourceCol.name, col);
          });
        }
      });

      // We match by name since ids are different. Names should also be unique for the same column type
      const customForAdd = allCustomColumns
        .filter((col) => {
          // Check if we already have this column (either by matching name or as a renamed column)
          return (
            !customColumnsInThisOrder.some(
              (existingCol) => existingCol.name === col.name
            ) && !existingColumnsByOriginalName.has(col.name)
          );
        })
        .map((col) => ({
          name: col.name,
          isVisible: false,
          columnType: ColumnType.Custom,
          columnOrder: col.columnOrder,
        }))
        .filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (col) =>
                col.name === item.name && col.columnType === item.columnType
            )
        );

      // Only mark columns for deletion if they don't exist in any source order and aren't renamed
      const customForDelete = customColumnsInThisOrder.filter((col) => {
        const sourceExists = allCustomColumns.some(
          (sourceCol) => sourceCol.name === col.name
        );
        const isRenamed = Array.from(
          existingColumnsByOriginalName.values()
        ).some((renamedCol) => renamedCol.id === col.id);
        return !sourceExists && !isRenamed;
      });

      // ORDER LABELS
      // The assumption is that all order label keys should be unique
      const allOrderLabels = [
        ...new Set(
          relevantResolvedOrders.flatMap((order: ResolvedOrder) =>
            order.labels.map((label) => label.key)
          )
        ),
      ];
      const orderLabelColumnsInThisOrder = thisOrder.orderColumns.filter(
        (col) => col.columnType === ColumnType.ParentOrderLabel
      );

      // Store existing column IDs by their name to preserve renamed columns
      orderLabelColumnsInThisOrder.forEach((col) => {
        existingColumnsByOriginalName.set(col.name, col);
      });

      const labelsForAdd = allOrderLabels
        .filter((ol) => {
          // Check if we already have this column (either by matching name or as a renamed column)
          return (
            !orderLabelColumnsInThisOrder.some(
              (existingCol) => existingCol.name === ol
            ) && !existingColumnsByOriginalName.has(ol)
          );
        })
        .map((ol) => ({
          name: ol,
          isVisible: false,
          columnType: ColumnType.ParentOrderLabel,
          columnOrder: 7,
        }));

      const labelsForDelete = orderLabelColumnsInThisOrder.filter((olc) => {
        const sourceExists = allOrderLabels.some((ol) => ol === olc.name);
        const isRenamed = Array.from(
          existingColumnsByOriginalName.values()
        ).some((renamedCol) => renamedCol.id === olc.id);
        return !sourceExists && !isRenamed;
      });

      // ITEM GROUPS
      // Grab all the possible item groups.
      const itemGroupSet = new Set<string>();
      data.forEach((trow) => {
        if (trow.itemGroups && trow.itemGroups.length > 0) {
          trow.itemGroups.forEach((item) => {
            const key = Object.keys(item)[0];
            itemGroupSet.add(key);
          });
        }
      });

      const itemGroupsInCurrentData = [...itemGroupSet];

      // Grab all the itemGroups that are currently recognized as columns
      const itemGroupColumnsInThisOrder = thisOrder.orderColumns.filter(
        (col) => col.columnType === ColumnType.ItemGroup
      );

      // Store existing column IDs by their name to preserve renamed columns
      // For item groups, we need to check both name and originalKey
      itemGroupColumnsInThisOrder.forEach((col) => {
        const extendedCol = col as ExtendedOrderColumn;
        // If this column has an originalKey, it's a renamed column
        if (extendedCol.originalKey) {
          existingColumnsByOriginalName.set(extendedCol.originalKey, col);
        } else {
          // Otherwise, store it by its name
          existingColumnsByOriginalName.set(col.name, col);
        }
      });

      const itemGroupsForAdd = itemGroupsInCurrentData
        .filter((igd) => {
          // Check if we already have this column, either:
          // 1. As a column with the same name or
          // 2. As a renamed column where the originalKey matches this name
          const existsByName = itemGroupColumnsInThisOrder.some(
            (existingCol) => existingCol.name === igd
          );

          const existsByOriginalKey = Array.from(
            existingColumnsByOriginalName.keys()
          ).includes(igd);

          return !existsByName && !existsByOriginalKey;
        })
        .map((ig) => ({
          name: ig,
          isVisible: !isProtan, // Hide itemGroup columns for Protan projects
          columnType: ColumnType.ItemGroup,
          // Use the itemGroupindex to determine order
          columnOrder: !Number.isNaN(Number(ig.at(-1)))
            ? Number(ig.at(-1)) + 2
            : 3,
          originalKey: ig, // Store original key for data lookup after renaming
        }));

      const itemGroupsForDelete = itemGroupColumnsInThisOrder.filter((col) => {
        const extendedCol = col as ExtendedOrderColumn;
        const originalKeyToCheck = extendedCol.originalKey || col.name;

        // Keep the column if its originalKey is still in the current data
        const stillExists =
          itemGroupsInCurrentData.includes(originalKeyToCheck);

        return !stillExists;
      });

      const toBeAdded = [...customForAdd, ...labelsForAdd, ...itemGroupsForAdd];
      const toBeDeleted = [
        ...customForDelete,
        ...labelsForDelete,
        ...itemGroupsForDelete,
      ];

      if (toBeAdded.length > 0 || toBeDeleted.length > 0) {
        const additionsPromise = updateOrder({
          input: {
            id: thisOrder.id,
            patch: {
              orderColumns: {
                create: toBeAdded.map((col) => ({
                  name: col.name,
                  isVisible: col.isVisible,
                  columnType: col.columnType,
                  columnOrder: col.columnOrder,
                })),
              },
            },
          },
        });
        const deletionsPromise = updateOrder({
          input: {
            id: thisOrder.id,
            patch: {
              orderColumns: {
                deleteById: toBeDeleted.map((col) => ({ id: col.id })),
              },
            },
          },
        });

        try {
          await Promise.all([additionsPromise, deletionsPromise]);
        } catch (e) {
          console.error(
            'Something went wrong while creating/cleaning up the columns to use. See error below:'
          );
          console.error(e);
        }
      }
    }

    cleanupAndMakeColumns();
  }, [
    resolvedOrders,
    data,
    thisOrder.id,
    thisOrder.orderColumns,
    updateOrder,
    isProtan,
  ]);

  const headerFontColor = useColorModeValue('gray.400', 'gray.500');

  const { logEvent } = useEventHistory(thisOrder.projectId);

  const hideColumn = useCallback(
    async (columnId: string) => {
      const { error } = await updateOrderColumn({
        input: { id: columnId, patch: { isVisible: false } },
      });
      if (error) {
        toast({
          title: 'Error',
          description: "Couldn't hide column",
          status: 'error',
        });
        throw error;
      } else {
        const columnName = thisOrder.orderColumns.find(
          (col) => col.id === columnId
        )?.name;

        logEvent({
          eventName: 'Column Hidden',
          eventType: EventType.Modified,
          resourceName: thisOrder.name,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [columnId],
            details: `Column ${columnName} hidden in ${thisOrder.name}`,
          },
        });
      }
    },
    [logEvent, thisOrder.name, thisOrder.orderColumns, toast, updateOrderColumn]
  );

  const renameColumn = useCallback(
    async (columnId: string | null, newName?: string) => {
      if (!columnId) {
        // This is the completion case - clear the renaming state
        setRenamingColumnId(null);
        return;
      }

      // Find the actual column ID for default columns
      const column = columnIdToColumn(columnId, thisOrder.orderColumns);
      const actualColumnId = column ? column.id : columnId;

      if (!newName) {
        // This is the start case - set the renaming state
        setRenamingColumnId(actualColumnId);
        return;
      }

      // This is the actual rename operation
      const columnToRename = thisOrder.orderColumns.find(
        (col) => col.id === actualColumnId
      );
      if (!columnToRename) {
        toast({
          title: 'Error',
          description: 'Column not found',
          status: 'error',
        });
        setRenamingColumnId(null);
        return;
      }

      // Create/update the column name mapping
      const columnNameMapping: Record<string, ColumnMapping> = {
        ...(thisOrder.columnNameMapping || {}),
      };

      // Add or update the display name for this column in the mapping
      // We use the column ID as the key in the mapping rather than the original name
      columnNameMapping[actualColumnId] = {
        id: actualColumnId,
        newName: newName,
      };

      // Update ONLY the mapping in the database - don't change the actual column name
      const { error } = await updateOrder({
        input: {
          id: thisOrder.id,
          patch: {
            columnNameMapping,
          },
        },
      });

      if (error) {
        toast({
          title: 'Error',
          description: 'Failed to rename column',
          status: 'error',
        });
        throw error;
      } else {
        logEvent({
          eventName: 'Table Column Renamed',
          eventType: EventType.Modified,
          resourceName: thisOrder.name,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [thisOrder.id],
          },
        });
      }

      // Clear the renaming state after successful rename
      setRenamingColumnId(null);
    },
    [thisOrder, updateOrder, toast]
  );

  const { selectDbIds, setSelectedDbIds } = useSelection();

  const onSelectLinkedElements = useCallback(
    (event: React.MouseEvent, row: Row<OrderEntryTableRow>) => {
      event.stopPropagation();
      const ids = fromPairs(
        row.original.resolvedElementIdentifiersDbid?.map(
          ({ modelUrn, dbIds }) => [modelUrn, dbIds]
        ) ?? []
      );
      if (event.shiftKey) {
        selectDbIds(ids);
      } else {
        setSelectedDbIds(ids);
      }
    },
    [selectDbIds, setSelectedDbIds]
  );
  const updateAggregation = useCallback(
    (action: 'update' | 'delete', columnId: string, value?: string) => {
      setAggregation((currentAggregation) => {
        const newAgggregation: Record<string, string> = {
          ...currentAggregation,
        };
        if (action === 'update' && value !== undefined) {
          newAgggregation[columnId] = value;
        } else if (action === 'delete' && columnId in newAgggregation) {
          delete newAgggregation[columnId];
        }

        // Aggregation in the state contains the ids but we need to transform these into the type required in the DB
        const aggregationForDB = Object.keys(newAgggregation).map((colId) => {
          const relatedColumn = columnIdToColumn(colId, thisOrder.orderColumns);
          if (!relatedColumn) {
            // Show toast and throw and error
            throw new Error(`Failed to find column for id: ${colId}`);
          }
          return {
            columnName: relatedColumn.name,
            columnType: relatedColumn.columnType,
            aggregationValue: newAgggregation[colId],
          };
        });

        // update the order
        updateOrder({
          input: {
            id: thisOrder.id,
            patch: {
              aggregatedOrderAggregation: aggregationForDB,
            },
          },
        }).then(({ error }) => {
          if (error) {
            toast({
              title: 'Error',
              description: 'Failed to store the aggregation state',
              status: 'error',
            });
            throw error;
          }
        });

        return newAgggregation;
      });
    },
    [thisOrder.orderColumns, thisOrder.id, updateOrder, toast]
  );

  const { isHistoryDrawerOpen, resolvedStates } = useProjectHistory();

  const columns = useMemo(
    () =>
      getColumns(
        headerFontColor,
        thisOrder.orderColumns,
        aggregation,
        grouping,
        updateAggregation,
        hideColumn,
        renameColumn,
        translation,
        onSelectLinkedElements,
        renamingColumnId,
        isHistoryDrawerOpen,
        thisOrder.columnNameMapping,
        isProtan
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      headerFontColor,
      thisOrder.orderColumns,
      aggregation, // Keep aggregation in deps to trigger re-creation when it changes
      grouping,
      updateAggregation,
      hideColumn,
      renameColumn,
      translation,
      onSelectLinkedElements,
      renamingColumnId,
      thisOrder.columnNameMapping,
      rowSelection,
      isHistoryDrawerOpen,
    ]
  );

  const columnOrder: ColumnOrderState = useMemo(() => {
    // Create the column order array from sorted columns first
    const sortedVisibleColumns = thisOrder.orderColumns
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));

    // First, always include select and expander columns
    const order = ['select', 'expander'];

    // Then add all other columns in the exact order from the database
    sortedVisibleColumns.forEach((col) => {
      // For predefined columns, use their standard table IDs
      if (
        ![
          ColumnType.Custom,
          ColumnType.ParentOrderLabel,
          ColumnType.ItemGroup,
        ].includes(col.columnType)
      ) {
        // Map the column type to its standard ID (title, quantity, etc.)
        const tableId = columnTypeToId(col.columnType as DefaultColumnType);
        if (tableId) {
          order.push(tableId);
        }
      } else {
        // For custom columns, use their database IDs directly
        order.push(col.id);
      }
    });

    return order;
  }, [thisOrder.orderColumns]);

  // column visibility for the table - different from management
  // We need to register ALL columns with the table, even hidden ones
  const colsVisibility = useMemo(() => {
    // Map all columns to their visibility state
    const visibilityMap: Record<string, boolean> = {};

    // Set default visibility for standard columns
    const defaultColumns = [
      'title',
      'quantity',
      'unit',
      'color',
      'parentOrder',
      'parentOrderStatus',
      'unitPrice',
      'total',
      'linkedCount',
    ];

    // Initialize all standard columns as hidden by default
    defaultColumns.forEach((id) => {
      visibilityMap[id] = false;
    });

    // Always set these system columns to visible
    visibilityMap['select'] = true;
    visibilityMap['expander'] = true;

    // Update visibility based on the orderColumns configuration
    thisOrder.orderColumns.forEach((col) => {
      if (
        ![
          ColumnType.Custom,
          ColumnType.ParentOrderLabel,
          ColumnType.ItemGroup,
        ].includes(col.columnType)
      ) {
        // For standard columns, use the mapped table ID
        const tableId = columnTypeToId(col.columnType as DefaultColumnType);
        if (tableId) {
          visibilityMap[tableId] = col.isVisible !== false;
        }
      } else {
        // For custom columns, use the database ID directly
        visibilityMap[col.id] = col.isVisible !== false;
      }
    });

    return visibilityMap;
  }, [thisOrder.orderColumns]);

  // We need to map the database column IDs in the grouping array to the table's expected column IDs
  const validGrouping = useMemo(() => {
    return getValidGrouping(grouping, thisOrder.orderColumns);
  }, [grouping, thisOrder.orderColumns]);

  const spankingNewData = useMemo(() => {
    if (!isHistoryDrawerOpen || !resolvedStates || !resolvedStates.length) {
      return getTableData({
        data,
        columnFilters,
        aggregation,
        grouping: validGrouping,
        orderColumns: thisOrder.orderColumns,
      });
    }
    // This means that we are in history mode
    // we want to search for the same aggregated order in the other state so that we can include its unique rows if any

    const [refTableData, rowsInOtherState] = resolvedStates.map(
      (cState: any) => {
        const cStateOrders = cState.project.orders;
        if (!cStateOrders) return;
        const [aggregatedOrders, typicalOrders] = partition(
          cStateOrders,
          (order: any) => order.isAggregatedOrder
        );
        const aggOrderInCurrentState = aggregatedOrders.find(
          (aggOrder) => aggOrder.id === thisOrder.id
        );
        if (!aggOrderInCurrentState) return;
        const currentRows = getAggregatedOrderDiffingTableRows(
          aggOrderInCurrentState,
          typicalOrders
        );
        return currentRows;
      }
    );

    if (!refTableData || !rowsInOtherState) {
      if (refTableData) return refTableData;
      if (rowsInOtherState) return rowsInOtherState;
      return [];
    }

    const entriesUnionMap = new Map<string, OrderEntryTableRow>(
      refTableData.map((dt: OrderEntryTableRow) => [dt.id, dt])
    );
    for (const row of rowsInOtherState) {
      let entryToAdd = row;
      const entryInRef = entriesUnionMap.get(row.id);
      if (entryInRef) {
        entryToAdd = mergeEntries(row, entryInRef as any);
      }
      entriesUnionMap.set(row.id, entryToAdd);
    }
    return Array.from(entriesUnionMap.values());
  }, [
    validGrouping,
    data,
    columnFilters,
    aggregation,
    resolvedStates,
    isHistoryDrawerOpen,
    thisOrder.orderColumns,
    thisOrder.id,
  ]);

  const table = useReactTable({
    data: spankingNewData,
    columns: columns as ColumnDef<OrderEntryTableRow>[],
    defaultColumn: {
      minSize: 30,
      maxSize: 800,
    },
    columnResizeMode: 'onChange',
    state: {
      rowSelection,
      expanded,
      columnOrder,
      columnVisibility: colsVisibility,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    autoResetExpanded: false,
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const itemGroups = useMemo(() => {
    const nonEmptyItemGroups = data
      .map((tdata) => tdata.itemGroups)
      .filter((v) => !!v)
      .flat() as Record<string, string>[];
    const objToReturn: Record<string, string[]> = {};
    for (let igroup of nonEmptyItemGroups) {
      const keys = Object.keys(igroup);
      for (let key of keys) {
        const valueAtKey = igroup[key];
        const arrayToInsert = objToReturn[key];
        if (arrayToInsert && !arrayToInsert.includes(valueAtKey)) {
          objToReturn[key].push(valueAtKey);
        } else if (!arrayToInsert) {
          objToReturn[key] = [valueAtKey];
        }
      }
    }
    return objToReturn;
  }, [data]);

  const selectedRows = table.getSelectedRowModel().flatRows;
  const allRows = table.getRowModel().flatRows;

  useEffect(() => {
    handleRowSelection(selectedRows, allRows);
  }, [handleRowSelection, selectedRows, allRows, isIsolationToggleActive]);

  // Fix for TanStack Table subrow selection bug
  // See: https://github.com/TanStack/table/issues/4878
  useEffect(() => {
    const { rows } = table.getRowModel();
    rows.forEach((row) => {
      if (row.subRows.length > 0) {
        if (row.getIsAllSubRowsSelected() === true) {
          row.toggleSelected(true);
        } else if (row.getIsSomeSelected() === false) {
          row.toggleSelected(false);
        }
      }
    });
  }, [rowSelection, table]);

  const {
    currentItemIdSelection: initialItemSelection,
    setCurrentItemIdSelection,
    setSuccessfullyRetrievedCurrentItemIdSelection,
    isGettingCurrentItemIdSelection: shouldSetItemSelectionForSharing,
    setCurrentExpandedItemIds,
    setSuccessfullyRetrievedCurrentExpandedItemIds,
    isGettingCurrentExpandedItemIds: shouldSetExpandedIdsForSharing,
  } = useViewManager();

  // If initialItemSelection is set, set the selection when the component mounts
  useEffect(() => {
    if (initialItemSelection) {
      const initialSelectionState: RowSelectionState = {};
      initialItemSelection.forEach((id) => {
        initialSelectionState[id] = true;
      });
      setRowSelection(initialSelectionState);
    }
  }, [initialItemSelection]);

  useEffect(() => {
    if (shouldSetItemSelectionForSharing) {
      const selectedIdsToSet: string[] = [];
      if (rowSelection) {
        // Check if rowSelection is truthy
        const selectedRowKeys = Object.keys(rowSelection).filter(
          (key) => rowSelection[key]
        );
        selectedIdsToSet.push(...selectedRowKeys);
      } else {
        // Log if rowSelection is not available, as this might indicate an issue,
        // but still proceed to resolve the promise with an empty selection.
        console.warn(
          '[useAggregatedOrderTable] rowSelection was not available when attempting to share. Proceeding with empty selection.'
        );
      }
      setCurrentItemIdSelection(selectedIdsToSet);
      setSuccessfullyRetrievedCurrentItemIdSelection(true);
    }
  }, [
    rowSelection,
    selectedRows,
    setCurrentItemIdSelection,
    setSuccessfullyRetrievedCurrentItemIdSelection,
    shouldSetItemSelectionForSharing,
  ]);

  // useEffect for providing expanded item IDs for sharing
  useEffect(() => {
    if (shouldSetExpandedIdsForSharing) {
      let expandedIds: string[] = [];
      const currentTableExpandedState = table.getState().expanded;
      if (
        typeof currentTableExpandedState === 'boolean' &&
        currentTableExpandedState
      ) {
        // All rows are expanded, get all top-level row IDs
        expandedIds = table.getRowModel().rows.map((row) => row.id);
      } else if (typeof currentTableExpandedState === 'object') {
        // Specific rows are expanded
        expandedIds = Object.keys(currentTableExpandedState).filter(
          (key) => currentTableExpandedState[key]
        );
      }
      setCurrentExpandedItemIds(expandedIds);
      setSuccessfullyRetrievedCurrentExpandedItemIds(true);
    }
  }, [
    table, // table instance might be needed if getState() is not stable without it
    shouldSetExpandedIdsForSharing,
    setCurrentExpandedItemIds,
    setSuccessfullyRetrievedCurrentExpandedItemIds,
  ]);

  useEffect(() => {
    const newFilterState = getFilterState(
      thisOrder.aggregatedOrderFilters,
      thisOrder.orderColumns
    );
    setColumnFilters(newFilterState);
  }, [thisOrder.aggregatedOrderFilters, thisOrder.orderColumns]);

  // Helper function for getting the column type from standard ID
  const getColumnTypeFromStandardId = (columnId: string): ColumnType | null => {
    if (columnId === 'title') return ColumnType.Description;
    if (columnId === 'quantity') return ColumnType.Quantity;
    if (columnId === 'unit') return ColumnType.Unit;
    if (columnId === 'color') return ColumnType.Color;
    if (columnId === 'parentOrder') return ColumnType.ParentOrder;
    if (columnId === 'parentOrderStatus') return ColumnType.ParentOrderStatus;
    if (columnId === 'unitPrice') return ColumnType.UnitPrice;
    if (columnId === 'total') return ColumnType.Total;
    if (columnId === 'linkedCount') return ColumnType.LinkedCount;
    return null;
  };

  // Helper function for getting standard column header text
  const getStandardColumnHeaderText = useCallback(
    (column: any): string => {
      // Get the column type from the standard ID
      const columnType = getColumnTypeFromStandardId(column.id);

      if (columnType) {
        // Find the corresponding column in orderColumns
        const columnObject = thisOrder.orderColumns.find(
          (col) => col.columnType === columnType
        );

        if (
          columnObject &&
          thisOrder.columnNameMapping &&
          thisOrder.columnNameMapping[columnObject.id]
        ) {
          // Use the renamed value
          return thisOrder.columnNameMapping[columnObject.id].newName;
        }
      }

      // Fallback to the default translated name
      const defaultName = columnIdToColumnName(column.id);
      return getTranslatedColumnName(defaultName, translation);
    },
    [thisOrder.orderColumns, thisOrder.columnNameMapping, translation]
  );

  // Helper function for getting custom column header text
  const getCustomColumnHeaderText = useCallback(
    (column: any): string => {
      // Find the column object by its ID
      const columnObject = thisOrder.orderColumns.find(
        (col) => col.id === column.id
      );

      if (columnObject) {
        // Check if this column has been renamed
        if (
          thisOrder.columnNameMapping &&
          thisOrder.columnNameMapping[columnObject.id]
        ) {
          // Use the renamed value
          return thisOrder.columnNameMapping[columnObject.id].newName;
        }
        // Use the column's original name
        return columnObject.name;
      }

      // Fallback for unknown columns
      return column.id;
    },
    [thisOrder.orderColumns, thisOrder.columnNameMapping]
  );

  // Helper function to get the appropriate header text for a column
  const getColumnHeaderText = useCallback(
    (column: any, isStandardColumn: boolean): string => {
      if (isStandardColumn) {
        return getStandardColumnHeaderText(column);
      } else {
        return getCustomColumnHeaderText(column);
      }
    },
    [getStandardColumnHeaderText, getCustomColumnHeaderText]
  );

  const handleExportToXlsx = useCallback(() => {
    // TODO: Potentially add code to log to mixpanel this event.
    const relevantRows = table.getRowModel().rows;
    const headersToExclude = ['select', 'linkedCount', 'addColumn', 'expander'];

    // These are the actual columns that will be exported
    const columnsToExport = table
      .getAllColumns()
      .filter(
        (column) =>
          column.getIsVisible() && !headersToExclude.includes(column.id)
      );

    // Get the display names and values for export
    const displayHeaders: string[] = [];
    const rowData: any[][] = [];

    // Get headers first by mapping column IDs to actual names
    columnsToExport.forEach((column) => {
      // There are two types of columns to handle:
      // 1. Default columns like 'title', 'quantity', etc. (using standard IDs)
      // 2. Custom columns using their database IDs

      // First check if this is a standard column ID
      const isStandardColumn = [
        'title',
        'quantity',
        'unit',
        'color',
        'parentOrder',
        'parentOrderStatus',
        'unitPrice',
        'total',
      ].includes(column.id);

      // Get the appropriate header text for this column
      displayHeaders.push(getColumnHeaderText(column, isStandardColumn));
    });

    // Build row data
    relevantRows.forEach((row) => {
      const rowValues: any[] = [];
      columnsToExport.forEach((column) => {
        let value = row.getValue(column.id);

        // Special handling for title column to include group names
        if (column.id === 'title') {
          const isGrouping = row.getIsGrouped();
          if (isGrouping) {
            const groupingValue = row.groupingValue as string;
            if (!groupingValue) {
              const groupingColumnId = row.groupingColumnId;
              let groupingColumnName = groupingColumnId
                ? columnIdToColumnName(groupingColumnId)
                : '';
              if (
                !groupingColumnName ||
                groupingColumnName === groupingColumnId
              ) {
                const groupedCol = table
                  .getAllColumns()
                  .find((col) => col.id === groupingColumnId);
                groupingColumnName =
                  (groupedCol?.columnDef.meta as { name?: string })?.name || '';
              }
              value = `Missing ${groupingColumnName}`;
            } else {
              value = groupingValue;
            }
          }
        }

        // Format the value
        if (isNil(value)) {
          rowValues.push(null);
        } else if (
          value !== null &&
          typeof value === 'object' &&
          'value' in value &&
          value.value !== undefined
        ) {
          // Handle quantity objects
          const numValue =
            typeof value.value === 'number'
              ? value.value
              : parseFloat(String(value.value));

          rowValues.push(numValue);
        } else if (typeof value === 'number') {
          rowValues.push(value);
        } else {
          rowValues.push(String(value));
        }
      });
      rowData.push(rowValues);
    });

    console.log('Exporting with headers:', displayHeaders);

    exportTableToExcel({
      headers: displayHeaders,
      rows: rowData,
      filename: `Sparkel Export - ${thisOrder.name} - ${
        project?.name
      } - ${new Date().toUTCString()}.xlsx`,
    });
  }, [table, thisOrder, project?.name, translation, getColumnHeaderText]);

  // Helper function to get translated column names
  const getTranslatedColumnName = (
    columnName: string,
    translator: (key: string) => string
  ): string => {
    // Check if this is a special column that needs translation
    const specialColumns: Record<string, string> = {
      Title: 'title',
      Quantity: 'quantity',
      Unit: 'unit',
      Color: 'color',
      Table: 'parentOrder',
      'Table Status': 'parentOrderStatus',
      'Unit cost': 'unitPrice',
    };

    // If it's a special column, use the translation
    if (columnName in specialColumns) {
      return translator(
        `table:default-column-name.${specialColumns[columnName]}`
      );
    }

    // For other columns that might be in the column mapping
    const translationKey = thisOrder.columnNameMapping?.[columnName];
    if (translationKey) {
      return translator(`table:default-column-name.${translationKey}`);
    }

    // If not found in mappings, return the original name
    return columnName;
  };

  const handleColumnManagementSubmit = useCallback(
    async (orderCols: Array<OrderColumnPatch>) => {
      invariant(thisOrder, 'Order is not defined');
      const validOrderColumns = orderCols.filter(
        (orderCol) => !isNil(orderCol?.id)
      ) as OrderColumnDeepFragment[];

      // All valid order columns should exist in the DB should probably be an enforced invariant.

      // We need to separate real updates (visibility, order) from display name changes
      // Create a mapping of column ID to original column for quick lookup
      const originalColumnsById: Record<string, OrderColumnDeepFragment> = {};
      thisOrder.orderColumns.forEach((col) => {
        originalColumnsById[col.id] = col;
      });

      // Create patches for column visibility and order only
      const columnPatches = validOrderColumns.map((col) => ({
        id: col.id,
        patch: {
          isVisible: col.isVisible,
          columnOrder: col.columnOrder,
          // Don't change the actual column name in the database
        },
      }));

      // Create/update the column name mapping for display names
      const columnNameMapping: Record<string, ColumnMapping> = {
        ...(thisOrder.columnNameMapping || {}),
      };

      // Update the display name mapping for columns where the name changed
      validOrderColumns.forEach((col) => {
        const originalCol = originalColumnsById[col.id];

        // If the submitted name is different from the original column name,
        // we need to store this in the mapping
        if (originalCol && col.name !== originalCol.name) {
          // Add or update the mapping entry
          columnNameMapping[col.id] = {
            id: col.id,
            newName: col.name,
          };
        }
        // If the name is the same as the original, and there's a mapping entry,
        // we should remove the entry as it's no longer needed
        else if (
          originalCol &&
          col.name === originalCol.name &&
          columnNameMapping[col.id]
        ) {
          delete columnNameMapping[col.id];
        }
      });

      // Create the patch object with columns and mapping
      const orderPatch: {
        orderColumns: {
          updateById: Array<{
            id: string;
            patch: {
              isVisible: boolean;
              columnOrder: number | null | undefined;
            };
          }>;
        };
        columnNameMapping: Record<string, ColumnMapping>;
      } = {
        orderColumns: {
          updateById: columnPatches,
        },
        columnNameMapping,
      };

      const { error } = await updateOrder({
        input: {
          id: thisOrder.id,
          patch: orderPatch,
        },
      });

      if (error) {
        toast({
          title: 'Error',
          description: 'Unable to save your column settings',
          status: 'error',
        });
        throw error;
      }
    },
    [toast, thisOrder, updateOrder]
  );

  return {
    table,
    selectedRows,
    itemGroups,
    handleExportToXlsx,
    setRowSelection,
    renamingColumnId,
    renameColumn,
    handleColumnManagementSubmit,
    columnNameMapping: thisOrder.columnNameMapping,
  };
};

type GetTableDataProps = {
  data: OrderEntryTableRow[];
  grouping: Array<string>;
  columnFilters: ColumnFiltersState;
  aggregation: Record<string, string>;
  orderColumns: ExtendedOrder['orderColumns'];
};

export function getTableData({
  data,
  grouping,
  columnFilters,
  aggregation,
  orderColumns,
}: GetTableDataProps) {
  // const restOfMap = thisOrder.orderColumns.filter(col => [ColumnType.Custom, ColumnType.ItemGroup, ColumnType.ParentOrderLabel].includes(col.columnType)).map(col => ({ id: col.id, accessorKey: col.id }));
  const specialColsMap = orderColumns
    .filter((col) =>
      [
        ColumnType.Custom,
        ColumnType.ItemGroup,
        ColumnType.ParentOrderLabel,
      ].includes(col.columnType)
    )
    .map((col) => ({ id: col.id, name: col.name, type: col.columnType }));
  const customColsMap = specialColsMap
    .filter((col) => [ColumnType.Custom].includes(col.type))
    .map((col) => ({ id: col.id, accessorKey: col.id }));
  const itemGroupColsMap = specialColsMap
    .filter((col) => col.type === ColumnType.ItemGroup)
    .map((col) => ({ id: col.id, accessorKey: col.name }));
  const accessorKeyMap = [
    { id: 'title', accessorKey: 'name' },
    { id: 'unit', accessorKey: 'unit' },
    { id: 'parentOrder', accessorKey: 'parentOrder.name' },
    { id: 'color', accessorKey: 'color' },
    { id: 'linkedCount', accessorKey: 'linkedCount' },
    { id: 'parentOrderStatus', accessorKey: 'parentOrder.status' },
    { id: 'unitPrice', accessorKey: 'unitPrice' },
    { id: 'total', accessorKey: 'total' },
    { id: 'quantity', accessorKey: 'resolvedQuantity' },
    ...customColsMap,
    ...itemGroupColsMap,
  ];
  // console.log('the accessor key Map: ', accessorKeyMap);
  const mappedGrouping: string[] = grouping
    .map((g) => {
      const correspondingKey = accessorKeyMap.find((k) => k.id === g);
      return correspondingKey?.accessorKey || g;
    })
    .filter(Boolean);
  const mappedColFilters = columnFilters.map((flt) => {
    const correspondingKey = accessorKeyMap.find((k) => k.id === flt.id);
    return {
      ...flt,
      id: correspondingKey?.accessorKey || flt.id,
    };
  });
  const mappedAggregations = Object.fromEntries(
    Object.entries(aggregation).map(([k, v]) => {
      const mappedKey = accessorKeyMap.find((m) => m.id === k);
      return [mappedKey?.accessorKey || k, v];
    })
  );
  return processTableData(
    data,
    specialColsMap,
    mappedColFilters,
    mappedGrouping,
    mappedAggregations
  );
}

export function getValidGrouping(
  grouping: string[],
  orderColumns: ExtendedOrder['orderColumns']
): string[] {
  if (!grouping.length) return [];

  // Create a mapping from database column IDs to table column IDs
  const dbToTableIdMap = new Map<string, string>();

  // Map all column IDs in thisOrder.orderColumns to their equivalent table column IDs
  orderColumns.forEach((col) => {
    if (
      ![
        ColumnType.Custom,
        ColumnType.ParentOrderLabel,
        ColumnType.ItemGroup,
      ].includes(col.columnType)
    ) {
      // For standard columns, use the mapped table ID
      const tableId = columnTypeToId(col.columnType as DefaultColumnType);
      if (tableId) {
        dbToTableIdMap.set(col.id, tableId);
      }
    } else {
      // For custom columns, use the database ID directly
      dbToTableIdMap.set(col.id, col.id);
    }
  });

  // Now map the grouping array using this mapping
  return grouping.map((dbColumnId) => {
    // If we have a direct mapping for this ID, use it
    if (dbToTableIdMap.has(dbColumnId)) {
      return dbToTableIdMap.get(dbColumnId)!;
    }

    // If not, just return the ID as is (could be a special column ID already)
    return dbColumnId;
  });
}

export function getFilterState(
  aggregatedOrderFilters: ExtendedOrder['aggregatedOrderFilters'],
  orderColumns: ExtendedOrder['orderColumns']
) {
  const newFilterState: {
    id: string;
    value: FilterValueType;
  }[] = [];

  aggregatedOrderFilters?.forEach((filter) => {
    if (!filter.columnName || !filter.predicate || !filter.predicateValues) {
      return;
    }

    // Get the columnId for this filter - we need to map it to a table column ID
    let columnId: string | null = null;

    // First try to find the column in thisOrder.orderColumns
    const columnObject = orderColumns.find(
      (col) =>
        col.name === filter.columnName && col.columnType === filter.columnType
    );

    if (columnObject) {
      // If we found the column, determine the table column ID
      if (
        ![
          ColumnType.Custom,
          ColumnType.ParentOrderLabel,
          ColumnType.ItemGroup,
        ].includes(columnObject.columnType)
      ) {
        // For standard columns, use the mapped table ID
        columnId = columnTypeToId(columnObject.columnType as DefaultColumnType);
      } else {
        // For custom columns, use the database ID directly
        columnId = columnObject.id;
      }
    } else {
      // Otherwise, try the old way as a fallback
      columnId = columnNameToColumnId(
        filter.columnName,
        orderColumns,
        filter.columnType
      );
    }

    // Add the filter with the table column ID
    if (columnId) {
      const existingFilter = newFilterState.find((f) => f.id === columnId);

      if (existingFilter) {
        existingFilter.value.push({
          predicate: filter.predicate,
          predicateValues: filter.predicateValues,
        });
      } else {
        newFilterState.push({
          id: columnId,
          value: [
            {
              predicate: filter.predicate,
              predicateValues: filter.predicateValues,
            },
          ],
        });
      }
    }
  });
  return newFilterState;
}

const getFilterFunction = (
  row: Row<OrderEntryTableRow>,
  columnId: string,
  filterValue: FilterValueType
) => {
  return filterValue.every((filter) => {
    const filterFn = filterFunctions[filter.predicate];
    if (
      filter.predicate === PredicateType.Exists ||
      filter.predicate === PredicateType.NotExists
    ) {
      return filterFn(row, columnId, undefined, () => null);
    }
    return filter.predicateValues.some((value) =>
      filterFn(row, columnId, value, () => null)
    );
  });
};

export const transformOrderEntry = (
  orderEntry: ResolvedOrderEntry,
  order: ResolvedOrder,
  thisOrderId: string
) => {
  const transformedEntry: OrderEntryTableRow = {
    id: orderEntry.id,
    thisOrderId,
    name: orderEntry.name,
    resolvedQuantity: orderEntry.resolvedQuantity,
    unit: orderEntry.unit,
    unitPrice: orderEntry.unitPrice ?? 0,
    createdAt: orderEntry.createdAt,
    updatedAt: orderEntry.updatedAt,
    parentOrder: {
      id: order.id,
      name: order.name,
      status: order.status,
      orderColumns: order.orderColumns.map((column) => {
        return {
          id: column.id,
          name: column.name,
        };
      }),
      labels: order.labels,
    },
    currency: orderEntry.currency,
    customFields: orderEntry.customFields
      .filter(
        (field) =>
          !!order.orderColumns.find(
            (column) => column.id === field.orderColumnId
          )
      )
      .map((field) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const orderColumnName = order.orderColumns.find(
          (column) => column.id === field.orderColumnId
        )!.name;
        return {
          ...field,
          orderColumnName,
        };
      }),
    projectId: order.projectId,
    linkedCount: flatMap(
      orderEntry.resolvedElementIdentifiersDbid.flatMap(({ dbIds }) => dbIds)
    ).length,
    resolvedElementIdentifiersDbid: orderEntry.resolvedElementIdentifiersDbid,
    total:
      (orderEntry.unitPrice ?? 0) *
      (typeof orderEntry.resolvedQuantity?.value === 'number'
        ? orderEntry.resolvedQuantity.value
        : 0),
    subRows: (orderEntry.subRows || []).map((subOrderEntry) =>
      transformOrderEntry(subOrderEntry, order, thisOrderId)
    ),
    color: orderEntry.color,
  };

  return transformedEntry;
};

export const transFormAndFlattenOrderEntries = (
  order: ResolvedOrder,
  thisOrderId: string
) => {
  const transformedEntries: Array<OrderEntryTableRow> = [];
  // Add a base case here to exit the recursion
  for (let i = 0; i < order.orderEntries.length; i++) {
    recTransform(order.orderEntries[i], order, transformedEntries, thisOrderId);
  }
  return transformedEntries;
};

const recTransform = (
  orderEntry: ResolvedOrderEntry,
  order: ResolvedOrder,
  retVal: Array<OrderEntryTableRow>,
  thisOrderId: string,
  parentItemGroups: Array<Record<string, string>> = [],
  parentName = '',
  depth = 0
) => {
  const transformedEntry: Required<OrderEntryTableRow> = {
    id: `${orderEntry.id}-${orderEntry.name}`,
    thisOrderId,
    name: orderEntry.name,
    resolvedQuantity: orderEntry.resolvedQuantity,
    unit: orderEntry.unit ?? null,
    unitPrice: orderEntry.unitPrice ?? 0,
    createdAt: orderEntry.createdAt,
    updatedAt: orderEntry.updatedAt,
    parentOrder: {
      id: order.id,
      name: order.name,
      status: order.status,
      orderColumns: order.orderColumns.map((column) => {
        return {
          id: column.id,
          name: column.name,
        };
      }),
      labels: order.labels,
    },
    currency: orderEntry.currency,
    customFields: orderEntry.customFields
      .filter(
        (field) =>
          !!order.orderColumns.find(
            (column) => column.id === field.orderColumnId
          )
      )
      .map((field) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const orderColumnName = order.orderColumns.find(
          (column) => column.id === field.orderColumnId
        )!.name;
        return {
          ...field,
          orderColumnName,
        };
      }),
    projectId: order.projectId,
    linkedCount: flatMap(
      orderEntry.resolvedElementIdentifiersDbid.flatMap(({ dbIds }) => dbIds)
    ).length,
    resolvedElementIdentifiersDbid: orderEntry.resolvedElementIdentifiersDbid,
    total:
      (orderEntry.unitPrice ?? 0) *
      (typeof orderEntry.resolvedQuantity?.value === 'number'
        ? orderEntry.resolvedQuantity.value
        : 0),
    subRows: [],
    color: orderEntry.color,
    itemGroups: [...parentItemGroups],
  };

  if (depth > 0) {
    transformedEntry.itemGroups.push({ [`itemGroup${depth}`]: parentName });
  }
  if (!orderEntry.subRows || orderEntry.subRows.length === 0) {
    retVal.push(transformedEntry);
  }
  // TODO: Using a foreach here but will switch to a good ol for loop for speed
  // TODO: Remember to test if we can get TCO
  orderEntry.subRows?.forEach((subRow) => {
    recTransform(
      subRow,
      order,
      retVal,
      thisOrderId,
      transformedEntry.itemGroups,
      orderEntry.name,
      depth + 1
    );
  });
};

const filterFunctions: {
  [key in PredicateType]: FilterFn<OrderEntryTableRow>;
} = {
  [PredicateType.Contains]: (row, columnId, filterValue: string) => {
    const rowValue = row.getValue<string>(columnId);
    return (
      !!rowValue &&
      rowValue.toLowerCase().includes(filterValue.toString().toLowerCase())
    );
  },
  [PredicateType.Equals]: (row, columnId, filterValue: string) => {
    const rowValue = row.getValue<string>(columnId);
    return !!rowValue && rowValue === filterValue;
  },
  [PredicateType.AnyOf]: (row, columnId, filterValue: string) => {
    const rowValue = row.getValue<string>(columnId);
    return !!rowValue && rowValue === filterValue;
  },
  [PredicateType.GreaterThan]: (row, columnId, filterValue: string) => {
    const rowValue = row.getValue(columnId);

    if (
      !!(rowValue as QuantityWithUnits) &&
      (rowValue as QuantityWithUnits).value
    ) {
      return (
        parseFloat(formatQuantity(rowValue as QuantityWithUnits)) >
        parseFloat(filterValue)
      );
    }

    return (
      (rowValue as string) != null &&
      parseFloat(rowValue as string) > parseFloat(filterValue)
    );
  },
  [PredicateType.LessThan]: (row, columnId, filterValue: string) => {
    const rowValue = row.getValue(columnId);

    if (
      !!(rowValue as QuantityWithUnits) &&
      (rowValue as QuantityWithUnits).value
    ) {
      return (
        parseFloat(formatQuantity(rowValue as QuantityWithUnits)) <
        parseFloat(filterValue)
      );
    }

    return (
      (rowValue as string) != null &&
      parseFloat(rowValue as string) < parseFloat(filterValue)
    );
  },
  [PredicateType.Exists]: (row, columnId) => !!row.getValue(columnId),
  [PredicateType.NotExists]: (row, columnId) => !row.getValue(columnId),
  [PredicateType.NotContains]: (row, columnId, filterValue) => {
    const rowValue = row.getValue<string>(columnId);
    return (
      !rowValue ||
      !rowValue.toLowerCase().includes(filterValue.toString().toLowerCase())
    );
  },
  [PredicateType.NotEquals]: (row, columnId, filterValue) => {
    const rowValue = row.getValue<string>(columnId);
    return !rowValue || rowValue !== filterValue;
  },
  [PredicateType.FromRegex]: (row, columnId, filterValue) => {
    const rowValue = row.getValue<string>(columnId);
    const regex = createRegexFromInputString(filterValue);
    return regex.test(rowValue);
  },
};
